import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard,
  FileText,
  Wrench,
  Droplets,
  BellRing,
  User,
  Settings,
  LogOut,
  Users,
  BarChart3,
  QrCode,
  Boxes,
  Package,
  ScrollText,
  ShieldCheck,
  Database,
  Megaphone,
  Mail,
  HelpCircle,
  Info,
  Library,
} from "lucide-react";

const installerNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/certificates", label: "Certificates", icon: FileText },
  { to: "/bottle-activations", label: "Bottle Activations", icon: Droplets },
  { to: "/follow-ups", label: "Follow Ups", icon: BellRing },
  { to: "/resources", label: "Resource Center", icon: Library },
  { to: "/profile", label: "Profile", icon: User },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/help", label: "Help", icon: HelpCircle },
  { to: "/about", label: "About", icon: Info },
];

const adminNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/installers", label: "Installers", icon: Wrench },
  { to: "/certificates", label: "Certificates", icon: FileText },
  { to: "/follow-ups", label: "Follow Ups", icon: BellRing },
  { to: "/bottle-batches", label: "Bottle Batches", icon: Boxes },
  { to: "/product-codes", label: "Product Directory", icon: Package },
  { to: "/resources", label: "Resource Center", icon: Library },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/broadcast", label: "Broadcast", icon: Megaphone },
  { to: "/email-templates", label: "Email Templates", icon: Mail },
  { to: "/audit-log", label: "Audit Log", icon: ScrollText },
  { to: "/sessions", label: "Sessions", icon: ShieldCheck },
  { to: "/backups", label: "Data & Backups", icon: Database },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/users", label: "Users", icon: Users },
  { to: "/help", label: "Help", icon: HelpCircle },
  { to: "/about", label: "About", icon: Info },
];

export function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = user?.role === "super_admin" ? adminNav : installerNav;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="flex h-full w-64 flex-col border-r border-white/[0.06] bg-[#0A0A0A]" data-testid="sidebar">
      <div className="flex h-16 items-center border-b border-white/[0.06] px-6">
        <div className="h-8">
          <Logo className="h-10" />
        </div>
      </div>

      <div className="px-4 pt-5 pb-2">
        <p className="px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-600">
          {user?.role === "super_admin" ? "Administration" : "Workspace"}
        </p>
      </div>

      <nav className="flex-1 space-y-1 px-4">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-[#E30613]/[0.12] text-white"
                  : "text-neutral-400 hover:bg-white/[0.04] hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={`h-4 w-[2px] rounded-full transition-colors duration-200 ${
                    isActive ? "bg-[#E30613]" : "bg-transparent"
                  }`}
                />
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/[0.06] p-4">
        <button
          onClick={handleLogout}
          data-testid="nav-logout"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-400 transition-colors duration-200 hover:bg-[#E30613]/[0.12] hover:text-[#FF1C2A]"
        >
          <span className="h-4 w-[2px] rounded-full bg-transparent" />
          <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}
